/* eslint-disable @typescript-eslint/no-unused-vars */

import { abbrToState } from "shared/app/VerificationTemplate/slices/CountyWalkInTypeSlice/constants";
import { EnrollmentOption, EnrollmentSource } from "types/landlord/enrollment";
import { AlgoliaUser } from "views/renters/types";

const verboseEnrollmentType = (
  enrollmentType: string,
  lowercase: boolean = false
): string => {
  let verboseEnrollmentType = enrollmentType;
  if (enrollmentType === "direct") verboseEnrollmentType = "Required";
  if (enrollmentType === "opt_in") verboseEnrollmentType = "Opt-in";
  if (enrollmentType === "opt_out") verboseEnrollmentType = "Opt-out";

  if (lowercase) return verboseEnrollmentType.toLowerCase();

  return verboseEnrollmentType;
};

export const prepareAlgoliaData = (
  data: AlgoliaUser[],
  enrollmentOption: EnrollmentOption
) => {
  return data.map((renter) => {
    const enrollmentSource: EnrollmentSource = "algolia";
    const { units, manager, phone, email, ...property } = renter.property;
    const { boomCustomerId, pets, hasPets, ...customer } = renter.customer;
    const {
      _highlightResult,
      boomEnrollmentStatus,
      property: _property,
      customer: _customer,
      ...renterDetails
    } = renter;

    const ret = {
      ...renterDetails,
      property,
      customer: customer,
      opt_out: enrollmentOption.optOut,
      opt_in: enrollmentOption.optIn,
      is_default_enrollment: enrollmentOption.isDefaultEnrollment,
      from_source: enrollmentSource
    };

    return ret;
  });
};

/**
 * Calculates the count of renters by state from an array of renters
 * Groups non-default enrollment types into an "other" category
 */
export const groupedDefaultEnrollmentTypes = ({
  rentersToProcess,
  partnerDefaultEnrollmentType,
  defaultEnrollmentTypes
}: {
  rentersToProcess: AlgoliaUser[];
  partnerDefaultEnrollmentType: string | undefined;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
}): string[] => {
  if (!partnerDefaultEnrollmentType) return [];

  const stateCounts: Record<string, number> = {};
  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  rentersToProcess.forEach((renter) => {
    const state = renter.property.state;

    if (statesWithDefaultEnrollmentType.has(state))
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    else stateCounts["Other"] = (stateCounts["Other"] || 0) + 1;
  });

  const entries = Object.entries(stateCounts);

  const isSingleUnspecifiedState =
    entries.length === 1 && entries[0][0] === "Other";
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return [];

  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType)
    return [
      `${stateCounts["Other"]} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`
    ];

  return entries.map(
    ([state, count]) =>
      `${state === "Other" ? "Other" : abbrToState[state]}: ${count} ${state === "Other" ? verboseEnrollmentType(partnerDefaultEnrollmentType || "", true) : verboseEnrollmentType(optValueByState[state] || "", true)}`
  );
};

/**
 * Determines the enrollment type for a single renter based on their state
 * Returns 'opt in', 'opt out', or 'direct' based on the default enrollment types configuration
 */
export const getRenterEnrollmentType = ({
  state,
  defaultEnrollmentTypes,
  partnerDefaultEnrollmentType
}: {
  state: string;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  partnerDefaultEnrollmentType: string | undefined;
}): string[] => {
  if (!partnerDefaultEnrollmentType) return [];

  const stateEnrollmentType = defaultEnrollmentTypes.find(
    (type) => type.state_province === state
  );

  if (stateEnrollmentType)
    return [
      `${abbrToState[state]}: ${verboseEnrollmentType(stateEnrollmentType.default_enrollment_type)}`
    ];

  if (partnerDefaultEnrollmentType)
    return [`${verboseEnrollmentType(partnerDefaultEnrollmentType)}`];

  return [];
};

export const getEnrollmentsByStateCopy = (
  enrollmentsByState: Record<string, number>,
  partnerDefaultEnrollmentType: string | undefined,
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>
) => {
  if (!partnerDefaultEnrollmentType) return "";
  if (Object.keys(enrollmentsByState).length === 0) return "";

  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  const entries = Object.entries(enrollmentsByState);
  const totalUnscopedCount = entries.reduce(
    (acc, [state, count]) =>
      statesWithDefaultEnrollmentType.has(state) ? acc : acc + count,
    0
  );

  const isSingleUnspecifiedState =
    entries.length === 1 && !statesWithDefaultEnrollmentType.has(entries[0][0]);
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return "";

  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType)
    return `${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`;

  const filteredEntries = entries.filter(([state, count]) =>
    statesWithDefaultEnrollmentType.has(state)
  );
  const mappedEntries = filteredEntries.map(
    ([state, count]) =>
      `${abbrToState[state]}: ${count} ${verboseEnrollmentType(optValueByState[state] || "", true)}`
  );
  if (totalUnscopedCount > 0 && partnerDefaultEnrollmentType)
    mappedEntries.push(
      `Other: ${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType || "", true)}`
    );

  return mappedEntries.join(", ");
};
